/**
 * reader-style-preset (= 副次 spec) の React Context Provider。
 *
 * - useReaderStylePreset の return を Context value として配布
 * - body element に `dark` class + `--reader-font-base` CSS variable を apply
 *   (= Tailwind darkMode: 'class' と既存 component CSS cascade と協調)
 * - useReaderStylePresetContext consumer hook を提供
 *
 * 設計詳細は `.kiro/specs/reader-style-preset/design.md` を参照。
 */

import { createContext, useContext, useEffect, type ReactNode } from "react";
import { DEFAULT_PRESET, FONT_SIZE_TO_PX } from "./types";
import {
  useReaderStylePreset,
  type UseReaderStylePresetResult,
} from "./useReaderStylePreset";

const ReaderStylePresetContext = createContext<UseReaderStylePresetResult | null>(
  null,
);

export function ReaderStylePresetProvider({
  children,
}: {
  children: ReactNode;
}) {
  const value = useReaderStylePreset();

  // body element に theme class + data-theme attribute + font size CSS variable を apply。
  // Tailwind dark variant (= 既存) + globals.css の semantic color token (= Phase 5+ Tier 5B
  // 第 1 段 Phase 1 新規 data-theme) を並列 apply、 既存 reader UI は backward compat 保持。
  useEffect(() => {
    if (typeof document === "undefined") return;
    const body = document.body;
    if (value.preset.theme === "dark") {
      body.classList.add("dark");
      body.dataset.theme = "dark";
    } else {
      body.classList.remove("dark");
      body.dataset.theme = "light";
    }
    body.style.setProperty(
      "--reader-font-base",
      `${FONT_SIZE_TO_PX[value.preset.fontSize]}px`,
    );
  }, [value.preset.theme, value.preset.fontSize]);

  return (
    <ReaderStylePresetContext.Provider value={value}>
      {children}
    </ReaderStylePresetContext.Provider>
  );
}

// Provider 外で呼ばれた場合は DEFAULT_PRESET fallback を返す (= test 環境 / SSR safe)。
// updatePreset / resetPreset は no-op になる (= context なしで mutation できないため)。
// これにより既存 reader UI test を Provider wrap で更新する必要を回避する。
const FALLBACK_VALUE: UseReaderStylePresetResult = {
  preset: DEFAULT_PRESET,
  updatePreset: () => {
    // no-op outside provider
  },
  resetPreset: () => {
    // no-op outside provider
  },
};

export function useReaderStylePresetContext(): UseReaderStylePresetResult {
  const ctx = useContext(ReaderStylePresetContext);
  return ctx ?? FALLBACK_VALUE;
}
